import { useState, useEffect } from 'react';
import ObfuscatedEmail from '@/components/base/ObfuscatedEmail';

const COOKIE_KEY = 'vixtra_cookie_consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) setVisible(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[998]" />

      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[999] bg-white border-t border-stone-200 px-6 md:px-10 py-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-5">
          {/* Icon */}
          <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
            <i className="ri-shield-check-line text-green-600 text-xl"></i>
          </div>

          {/* Text */}
          <div className="flex-1">
            <p className="text-stone-800 text-sm font-semibold mb-1">We value your privacy</p>
            <p className="text-stone-500 text-xs leading-relaxed">
              Vixtra Inc. uses cookies to enhance your browsing experience and analyze site traffic. By continuing to use our website, you agree to our{' '}
              <button
                onClick={() => setShowPolicy(true)}
                className="text-green-600 underline underline-offset-2 cursor-pointer hover:text-green-700 transition-colors"
              >
                Cookie &amp; Privacy Policy
              </button>
              . You must accept to continue using this site.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => setShowPolicy(true)}
              className="whitespace-nowrap cursor-pointer border border-stone-300 hover:border-stone-400 text-stone-700 text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-200"
            >
              Learn More
            </button>
            <button
              onClick={handleAccept}
              className="whitespace-nowrap cursor-pointer bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors duration-200"
            >
              I Accept
            </button>
          </div>
        </div>
      </div>

      {/* Policy Modal */}
      {showPolicy && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl border border-stone-200 w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-stone-100">
              <h2 className="text-stone-900 font-bold text-lg">Cookie &amp; Privacy Policy</h2>
              <button
                onClick={() => setShowPolicy(false)}
                className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-pointer transition-colors rounded-md hover:bg-stone-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto px-8 py-6 flex flex-col gap-6 text-sm text-stone-600 leading-relaxed">
              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">1. Introduction</h3>
                <p>
                  Vixtra Inc. (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your privacy. This Cookie &amp; Privacy Policy explains how we collect, use, and safeguard information when you visit our website at vixtra.ca.
                </p>
              </div>

              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">2. What Are Cookies</h3>
                <p>
                  Cookies are small text files placed on your device when you visit a website. They help us remember your preferences, understand how you use our site, and improve your overall experience.
                </p>
              </div>

              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">3. How We Use Cookies</h3>
                <ul className="list-disc list-inside flex flex-col gap-1.5 text-stone-500">
                  <li><strong className="text-stone-700">Essential Cookies:</strong> Required for the website to function properly.</li>
                  <li><strong className="text-stone-700">Analytics Cookies:</strong> Help us understand how visitors interact with our site.</li>
                  <li><strong className="text-stone-700">Preference Cookies:</strong> Remember your settings and preferences for future visits.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">4. Information We Collect</h3>
                <p>
                  We may collect information such as your IP address, browser type, pages visited, and time spent on our site. Any personal information you voluntarily provide (e.g., via our contact form) is used solely to respond to your inquiries.
                </p>
              </div>

              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">5. Third-Party Sharing</h3>
                <p>
                  We do not sell, trade, or rent your personal information to third parties. We may share anonymized, aggregated data for analytical purposes only.
                </p>
              </div>

              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">6. Your Rights</h3>
                <p>
                  You have the right to access, correct, or request deletion of your personal data. To exercise these rights, please contact us at{' '}
                  <ObfuscatedEmail email="contact@Vixtra.ca" className="text-green-600 hover:underline cursor-pointer" />.
                </p>
              </div>

              <div>
                <h3 className="text-stone-900 font-semibold text-base mb-2">7. Policy Updates</h3>
                <p>
                  We may update this policy from time to time. Any changes will be posted on this page with an updated effective date. Continued use of our website constitutes acceptance of the revised policy.
                </p>
              </div>

              <p className="text-stone-400 text-xs">Last updated: April 2026</p>
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-5 border-t border-stone-100 flex justify-end gap-3">
              <button
                onClick={() => setShowPolicy(false)}
                className="whitespace-nowrap cursor-pointer border border-stone-300 hover:border-stone-400 text-stone-700 text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-200"
              >
                Close
              </button>
              <button
                onClick={handleAccept}
                className="whitespace-nowrap cursor-pointer bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors duration-200"
              >
                I Accept &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
